import { useState, useEffect, useCallback, useRef } from 'react';

export function usePersistedState<T>(key: string, defaultValue: T) {
  const saveTimeoutRef = useRef<NodeJS.Timeout>();
  const focusTimeoutRef = useRef<NodeJS.Timeout>();
  
  const [state, setState] = useState<T>(() => {
    try {
      const saved = localStorage.getItem(key);
      if (saved && saved !== 'undefined' && saved !== 'null') {
        const parsed = JSON.parse(saved);
        // Geçerli veri kontrolü
        if (parsed !== null && parsed !== undefined) {
          return parsed;
        }
      }
    } catch (error) {
      console.error(`❌ Error loading persisted state for ${key}:`, error);
      // Bozuk veriyi temizle
      localStorage.removeItem(key);
    }
    return defaultValue;
  });

  const setPersistedState = useCallback((value: T | ((prev: T) => T)) => {
    setState(prevState => {
      const newState = typeof value === 'function' ? (value as (prev: T) => T)(prevState) : value;
      
      // Debounce localStorage writes
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = setTimeout(() => {
        try {
          // Null/undefined değerleri kaydetme
          if (newState !== null && newState !== undefined) {
            localStorage.setItem(key, JSON.stringify(newState));
          } else {
            localStorage.removeItem(key);
          }
        } catch (error) {
          console.error(`❌ Error saving persisted state for ${key}:`, error);
        }
      }, 100); // Debounce süresini kısalt
      
      return newState;
    });
  }, [key]);

  // Focus handling disabled to prevent refresh loops
  // Only reload on page refresh, not on window focus

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      clearTimeout(saveTimeoutRef.current);
    };
  }, []);

  return [state, setPersistedState] as const;
}
